import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useReducedMotion } from 'framer-motion';
import { ArrowLeft, ArrowRight, ArrowUpRight } from 'lucide-react';

const DELAY = 6000;
const SWIPE_THRESHOLD = 48;

/**
 * Carrousel des réalisations : une seule vue à la fois, pleine largeur.
 * Il défile seul jusqu'à la première action de l'utilisateur (flèche, puce,
 * clavier ou swipe), puis se tait définitivement. Il se met aussi en pause au
 * survol et hors écran, et ne démarre pas si les animations sont réduites.
 */
export default function Showcase({ items }) {
  const [index, setIndex] = useState(0);
  const [auto, setAuto] = useState(true);
  // Deux causes de pause distinctes : sortir du survol ne doit pas relancer
  // un carrousel qui se trouve hors de l'écran, et réciproquement.
  const [hovered, setHovered] = useState(false);
  const [offscreen, setOffscreen] = useState(false);
  const reduced = useReducedMotion();
  const rootRef = useRef(null);
  const touchStart = useRef(null);

  const count = items.length;
  const running = auto && !hovered && !offscreen && !reduced && count > 1;

  const goTo = useCallback((next) => setIndex(((next % count) + count) % count), [count]);

  // Toute action explicite rend la main à l'utilisateur, sans retour au défilement.
  const take = useCallback((next) => {
    setAuto(false);
    goTo(next);
  }, [goTo]);

  useEffect(() => {
    if (!running) return undefined;
    const timer = setTimeout(() => setIndex((i) => (i + 1) % count), DELAY);
    return () => clearTimeout(timer);
  }, [running, index, count]);

  // Hors de l'écran, on ne fait rien tourner.
  useEffect(() => {
    const node = rootRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setOffscreen(!entry.isIntersecting),
      { threshold: 0.25 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const onKeyDown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      take(index - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      take(index + 1);
    }
  };

  const onTouchStart = (event) => {
    touchStart.current = event.touches[0].clientX;
  };

  const onTouchEnd = (event) => {
    if (touchStart.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStart.current;
    touchStart.current = null;
    if (Math.abs(delta) < SWIPE_THRESHOLD) return;
    take(delta < 0 ? index + 1 : index - 1);
  };

  return (
    <div
      className="cv-carousel"
      ref={rootRef}
      role="group"
      aria-roledescription="carrousel"
      aria-label="Réalisations"
      tabIndex={0}
      onKeyDown={onKeyDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="cv-carousel-viewport">
        <div
          className="cv-carousel-track"
          style={{ transform: `translate3d(${-index * 100}%, 0, 0)` }}
        >
          {items.map((item, i) => {
            const active = i === index;
            return (
              <article
                className={`cv-slide ${active ? 'is-active' : ''}`}
                key={item.id}
                aria-hidden={!active}
                aria-roledescription="diapositive"
                aria-label={`${i + 1} sur ${count} : ${item.title}`}
              >
                <div className="cv-shell cv-slide-inner">
                  <figure className="cv-slide-media">
                    <img src={item.image} alt={item.title} loading="lazy" />
                    {item.colors && item.colors.length > 0 && (
                      <ul className="cv-slide-colors" aria-label="Palette du projet">
                        {item.colors.map((color) => (
                          <li key={color}>
                            <span style={{ backgroundColor: color }} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </figure>

                  <div className="cv-slide-body">
                    <p className="cv-slide-context">{item.context}</p>
                    <h3 className="cv-slide-title">{item.title}</h3>
                    <p className="cv-slide-blurb">{item.blurb}</p>

                    <dl className="cv-slide-meta">
                      <div>
                        <dt>Rôle</dt>
                        <dd>{item.role}</dd>
                      </div>
                      <div>
                        <dt>Année</dt>
                        <dd>{item.year}</dd>
                      </div>
                    </dl>

                    <Link
                      to={item.href}
                      className="cv-card-link"
                      tabIndex={active ? 0 : -1}
                    >
                      Voir le projet
                      <ArrowUpRight size={16} strokeWidth={1.75} />
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      <div className="cv-shell cv-carousel-controls">
        <div className="cv-carousel-dots">
          {items.map((item, i) => (
            <button
              type="button"
              key={item.id}
              className={`cv-dot ${i === index ? 'is-active' : ''}`}
              onClick={() => take(i)}
              aria-label={`Aller à ${item.title}`}
              aria-current={i === index}
            >
              <span
                className="cv-dot-fill"
                style={running && i === index ? { animationDuration: `${DELAY}ms` } : undefined}
                data-running={running && i === index ? 'true' : 'false'}
                key={`${item.id}-${index}-${running}`}
              />
            </button>
          ))}
        </div>

        <div className="cv-carousel-arrows">
          <button
            type="button"
            className="cv-arrow"
            onClick={() => take(index - 1)}
            aria-label="Réalisation précédente"
          >
            <ArrowLeft size={18} strokeWidth={1.75} />
          </button>
          <button
            type="button"
            className="cv-arrow"
            onClick={() => take(index + 1)}
            aria-label="Réalisation suivante"
          >
            <ArrowRight size={18} strokeWidth={1.75} />
          </button>
        </div>
      </div>
    </div>
  );
}
